import * as React from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EchoProfileCard } from '../EchoProfileCard';
import { TestChatCard } from '../TestChatCard';
import { RetrainEcho } from '../RetrainEcho';
import { StatsRow } from '../StatsRow';
import { ShareSection } from '../ShareSection';
import { VisibilitySection } from '../VisibilitySection';
import type { EchoProfileResponse, UpdatePricingRequest } from '@/types/echo_creation';

// Assets
import echoCreatedImage from '@/assets/images/webp/echo_created.webp';
import waveContactListSvg from '@/assets/images/svg/wave_element_contact_list.svg';

interface EchoDashboardStateProps {
  echo: EchoProfileResponse;
  draftId: string | null;
  isUpdating: boolean;
  onUpdateVisibility: (isPublic: boolean) => void;
  onUpdatePricing: (data: UpdatePricingRequest) => void;
  onRetrain: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
}

export const EchoDashboardState: React.FC<EchoDashboardStateProps> = ({
  echo,
  draftId,
  isUpdating,
  onUpdateVisibility,
  onUpdatePricing: _onUpdatePricing,
  onRetrain,
  onDelete,
  isDeleting = false,
}) => (
  <div className='relative size-full overflow-hidden bg-white'>
    {/* Wave Decoration Element */}
    <img
      src={waveContactListSvg}
      alt=''
      className='pointer-events-none absolute bottom-0 left-0 z-0 select-none'
    />

    <div className='custom-scrollbar relative z-10 size-full overflow-y-auto p-6 md:p-10'>
      {/* Header - Full Width (Unconstrained) */}
      <PageHeader title='ECHO PROFILE' titleClassName='text-brand-800' theme='dark' />

      {/* Main Content - Constrained Width (max-w-1215) */}
      <div className='mx-0 w-full max-w-1215 wide:mx-auto'>
        <div className='flex w-full flex-col gap-12'>
          {/* Top Grid: Profile + Chat */}
          <div className='flex flex-col gap-6 lg:grid lg:grid-cols-12 lg:gap-6'>
            <div className='flex flex-col gap-6 lg:col-span-5'>
              <EchoProfileCard
                name={echo.name || 'My Echo'}
                imageSrc={echo.profile_image_url ?? echoCreatedImage}
                idleVideoUrl={echo.idle_video_url}
                draftId={draftId ?? undefined}
                echoId={echo.id}
                {...(onDelete ? { onDelete } : {})}
                isDeleting={isDeleting}
              />

              {/* Desktop: Retrain button appears here */}
              <div className='hidden lg:block'>
                <RetrainEcho onRetrain={onRetrain} />
              </div>
            </div>
            <div className='lg:relative lg:col-span-7'>
              {/*
                  Test Chat Container
                  - Mobile: Negative margins to touch edges, normal flow.
                  - Desktop: Absolute positioning to fill grid cell height (matching left col).
                */}
              <div className='-mx-6 w-[calc(100%+48px)] md:mx-0 md:w-full lg:absolute lg:inset-0 lg:size-full'>
                <TestChatCard echo={echo} />
              </div>

              {/* Mobile: Retrain button (Normal flow below chat) */}
              <div className='mt-6 block lg:hidden'>
                <RetrainEcho onRetrain={onRetrain} />
              </div>
            </div>
          </div>
          {/* Stats - Now passing the echo object */}
          <StatsRow echo={echo} />
          <hr className='border-gray-300' />
          {/* Share */}
          <ShareSection echoName={echo.name || 'My Echo'} />
          <hr className='border-gray-300' />
          {/* Visibility */}
          <VisibilitySection
            isPublic={echo.is_public}
            onUpdate={onUpdateVisibility}
            disabled={isUpdating}
          />
        </div>
      </div>
    </div>
  </div>
);
